import { create } from 'zustand';
import { assetStore } from './assetStore';

interface AssetUrlState {
  /** assetId → objectURL for blobs already loaded this session. */
  urls: Record<string, string>;
}

export const useAssetUrlStore = create<AssetUrlState>(() => ({ urls: {} }));

/** Synchronous read for the pure rewriter; null = not loaded yet. */
export function assetUrl(id: string): string | null {
  return useAssetUrlStore.getState().urls[id] ?? null;
}

const inFlight = new Set<string>();

/** Load any not-yet-cached ids from IndexedDB → objectURL → store. Idempotent. */
export async function ensureAssetsLoaded(ids: string[]): Promise<void> {
  const have = useAssetUrlStore.getState().urls;
  const todo = ids.filter((id) => !have[id] && !inFlight.has(id));
  if (todo.length === 0) return;
  todo.forEach((id) => inFlight.add(id));
  await Promise.all(
    todo.map(async (id) => {
      try {
        const blob = await assetStore.get(id);
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        useAssetUrlStore.setState((s) => ({ urls: { ...s.urls, [id]: url } }));
      } finally {
        inFlight.delete(id);
      }
    }),
  );
}

/** Revoke every objectURL + clear the cache (call on doc load to avoid leaks). */
export function resetAssetUrls(): void {
  const { urls } = useAssetUrlStore.getState();
  for (const url of Object.values(urls)) URL.revokeObjectURL(url);
  inFlight.clear();
  useAssetUrlStore.setState({ urls: {} });
}
